import React, { useEffect, useMemo, useState } from 'react';
import { diffLines, Change } from 'diff';
import { getStepHistory, restoreStepFromVersion, StepHistoryEntry } from '../lib/api';
import { useStore } from '../store';
import { I, Station } from './Shared';

export function HistoryModal({ stepKey, onClose }: { stepKey: string; onClose: () => void }) {
  const project = useStore((s) => s.project);
  const [entries, setEntries] = useState<StepHistoryEntry[] | null>(null);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [leftIdx, setLeftIdx] = useState(0);
  const [rightIdx, setRightIdx] = useState(1);
  const [artifactIdx, setArtifactIdx] = useState(0);
  const [restoringVid, setRestoringVid] = useState<string | null>(null);

  async function handleSwitch(entry: StepHistoryEntry) {
    if (!project || restoringVid) return;
    if (entry.versionId === activeVersionId) return;
    setRestoringVid(entry.versionId);
    setError(null);
    try {
      const result = await restoreStepFromVersion(project.id, stepKey, entry.versionId);
      // Surgical in-place update: swap the project spec in the store. No
      // stream close, no UI state reset — the SSE connection and all panel
      // state (expanded phases, active phase, etc.) are preserved.
      useStore.setState({
        project: result.project,
        activeStepVersions: result.activeStepVersions,
      });
      setActiveVersionId(result.activeVersionId);
    } catch (e: any) {
      setError(e?.message ?? 'failed to switch version');
    } finally {
      setRestoringVid(null);
    }
  }

  useEffect(() => {
    if (!project) return;
    // Use the project id for server projects, 'demo' for demo projects
    const fetchId = project.id.startsWith('p_') ? project.id : 'demo';
    let cancelled = false;
    void (async () => {
      try {
        const result = await getStepHistory(fetchId, stepKey);
        if (!cancelled) {
          setEntries(result.history);
          setActiveVersionId(result.activeVersionId);
          const activeIdx = result.activeVersionId
            ? result.history.findIndex((h) => h.versionId === result.activeVersionId)
            : -1;
          const focus = activeIdx >= 0 ? activeIdx : result.history.length - 1;
          if (result.history.length >= 2) {
            setLeftIdx(Math.max(0, focus - 1));
            setRightIdx(Math.max(0, focus));
          } else if (result.history.length === 1) {
            setLeftIdx(0);
            setRightIdx(0);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'failed to load history');
      }
    })();
    return () => { cancelled = true; };
  }, [project, stepKey]);

  const left = entries?.[leftIdx];
  const right = entries?.[rightIdx];

  const artifactNames = useMemo(() => {
    const set = new Set<string>();
    if (left) for (const a of left.artifacts) set.add(a.name);
    if (right) for (const a of right.artifacts) set.add(a.name);
    return Array.from(set);
  }, [left, right]);

  const currentName = artifactNames[artifactIdx] ?? '';
  const leftContent = left?.artifacts.find((a) => a.name === currentName)?.content ?? '';
  const rightContent = right?.artifacts.find((a) => a.name === currentName)?.content ?? '';
  const diff: Change[] = useMemo(() => diffLines(leftContent, rightContent), [leftContent, rightContent]);

  const addedLines = diff.filter((d) => d.added).reduce((n, d) => n + (d.value.split('\n').length - 1), 0);
  const removedLines = diff.filter((d) => d.removed).reduce((n, d) => n + (d.value.split('\n').length - 1), 0);

  const lineBg = (added: boolean | undefined, removed: boolean | undefined) =>
    added ? 'var(--ok-soft)' : removed ? 'var(--err-soft)' : 'transparent';
  const lineCol = (added: boolean | undefined, removed: boolean | undefined) =>
    added ? 'oklch(35% 0.10 150)' : removed ? 'oklch(42% 0.16 28)' : 'var(--ink-2)';

  return (
    <div className="hm-backdrop" onClick={onClose}>
      <div
        className="af card"
        style={{
          width: 1100,
          height: '88%',
          maxHeight: 820,
          padding: 0,
          background: 'var(--surface)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px var(--line)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="loz">HISTORY</span>
          <span className="t-h2">{stepKey}</span>
          {currentName && <span className="kbd">{currentName}</span>}
          <span style={{ flex: 1 }} />
          {entries && <span className="t-micro muted">{entries.length} version{entries.length === 1 ? '' : 's'}</span>}
          <button className="btn btn--ghost btn--sm btn--icon" onClick={onClose}>{I.x}</button>
        </div>

        {error && <div style={{ padding: 16, color: 'var(--err)' }}>{error}</div>}

        {entries && entries.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* version list */}
            <div style={{ borderRight: '1px solid var(--line)', background: 'var(--bg-sunken)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="t-micro" style={{ padding: '12px 14px 6px' }}>Versions</div>
              <div className="scroll" style={{ flex: 1, padding: '0 8px 12px' }}>
                {entries.map((e, i) => {
                  const isActive = activeVersionId === e.versionId;
                  const isSelected = i === rightIdx;
                  return (
                    <div
                      key={e.versionId}
                      className="card"
                      style={{
                        padding: 12,
                        marginBottom: 6,
                        background: isSelected ? 'var(--surface)' : 'transparent',
                        border: isSelected ? '1px solid var(--ink)' : '1px solid var(--line)',
                        boxShadow: isSelected ? '0 0 0 1px var(--ink)' : 'none',
                        cursor: 'pointer',
                        opacity: restoringVid && restoringVid !== e.versionId ? 0.5 : 1,
                      }}
                      onClick={() => {
                        if (i > 0) { setLeftIdx(i - 1); setRightIdx(i); }
                        else { setLeftIdx(0); setRightIdx(0); }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <Station n={e.version} size="sm" />
                        <span className="t-h3">v{e.version}</span>
                        {isActive && <span className="loz loz--accent">active</span>}
                      </div>
                      <div className="t-small" style={{ color: 'var(--ink-2)', marginBottom: 6 }}>{causeLabel(e.cause)}</div>
                      {e.prompt && (
                        <div
                          style={{
                            marginBottom: 8,
                            padding: '6px 8px',
                            background: 'var(--surface-2)',
                            borderLeft: '2px solid var(--accent)',
                            borderRadius: 3,
                            fontSize: 11.5,
                            lineHeight: 1.45,
                            color: 'var(--ink-2)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 96,
                            overflow: 'auto',
                          }}
                          title={e.prompt}
                        >
                          <div
                            className="t-micro"
                            style={{ color: 'var(--ink-faint)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}
                          >
                            {promptLabel(e.cause)}
                          </div>
                          {e.prompt}
                        </div>
                      )}
                      <div className="t-micro muted" style={{ marginBottom: isActive ? 0 : 8 }}>{new Date(e.createdAt).toLocaleString()} · {e.artifacts.length} files</div>
                      {!isActive && project?.id.startsWith('p_') && (
                        <button
                          className="btn btn--sm"
                          style={{ width: '100%' }}
                          disabled={restoringVid !== null}
                          onClick={(ev) => { ev.stopPropagation(); handleSwitch(e); }}
                        >
                          {restoringVid === e.versionId ? 'Switching…' : `Switch to v${e.version}`}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {artifactNames.length > 1 && (
                <div style={{ padding: 12, borderTop: '1px solid var(--line)' }}>
                  <div className="label">Artifact</div>
                  <select className="select mono" style={{ fontSize: 12 }} value={artifactIdx} onChange={(e) => setArtifactIdx(Number(e.target.value))}>
                    {artifactNames.map((n, i) => <option key={n} value={i}>{n}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* diff */}
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid var(--line)' }}>
                <div style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8, borderRight: '1px solid var(--line)', background: 'var(--surface-2)' }}>
                  <span className="dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--err)' }} />
                  <span className="t-h3">v{left?.version ?? '?'} · before</span>
                  <span style={{ flex: 1 }} />
                  {removedLines > 0 && <span className="t-micro muted">-{removedLines} lines</span>}
                </div>
                <div style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)' }}>
                  <span className="dot" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)' }} />
                  <span className="t-h3">v{right?.version ?? '?'} · after</span>
                  <span style={{ flex: 1 }} />
                  {addedLines > 0 && <span className="t-micro muted">+{addedLines} lines</span>}
                </div>
              </div>
              <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12.5, lineHeight: 1.7 }}>
                {leftIdx === rightIdx ? (
                  <pre style={{ margin: 0, padding: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--ink-2)' }}>{rightContent || '(empty)'}</pre>
                ) : isHtmlOrSvg(currentName) ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, height: '100%' }}>
                    <iframe title="before" srcDoc={leftContent} sandbox="allow-same-origin" style={{ width: '100%', height: '100%', border: 'none', borderRight: '1px solid var(--line)' }} />
                    <iframe title="after" srcDoc={rightContent} sandbox="allow-same-origin" style={{ width: '100%', height: '100%', border: 'none' }} />
                  </div>
                ) : (
                  <div>
                    {diff.map((part, i) => {
                      const lines = part.value.split('\n');
                      return lines.map((line, li) => {
                        if (li === lines.length - 1 && line === '') return null;
                        const prefix = part.added ? '+' : part.removed ? '-' : ' ';
                        return (
                          <div
                            key={`${i}-${li}`}
                            style={{
                              display: 'flex',
                              background: lineBg(part.added, part.removed),
                              color: lineCol(part.added, part.removed),
                              padding: '0 14px',
                            }}
                          >
                            <span style={{ width: 14, color: part.added ? 'var(--ok)' : part.removed ? 'var(--err)' : 'var(--ink-faint)' }}>{prefix === ' ' ? '' : prefix}</span>
                            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1 }}>{line}</span>
                          </div>
                        );
                      });
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {entries && entries.length === 0 && (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-muted)' }}>
            No history yet — run this step to create the first version.
          </div>
        )}


        {/* footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', background: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', gap: 10 }}>
          {left && right && leftIdx !== rightIdx && (
            <span className="t-small muted">Comparing v{left.version} → v{right.version}</span>
          )}
          <span style={{ flex: 1 }} />
          <button className="btn btn--sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function causeLabel(cause: string): string {
  if (cause === 'bootstrap') return 'Initial run from your idea';
  if (cause.startsWith('edit:')) return 'Improvement requested';
  if (cause.startsWith('rerun:') && cause.endsWith(':budget')) return 'Re-run · budget exceeded';
  if (cause.startsWith('rerun:') && cause.endsWith(':failed')) return 'Re-run · failed';
  if (cause.startsWith('rerun:')) return 'Re-run';
  if (cause.startsWith('run-start:')) return 'Run started';
  if (cause.startsWith('run-failed:')) return 'Run failed';
  if (cause.startsWith('awaiting-input:')) return 'Awaiting your input';
  if (cause.startsWith('answer:')) return 'Answers submitted';
  if (cause.startsWith('outdated:')) return 'Marked outdated by upstream change';
  if (cause.startsWith('pin:')) return 'Pinned';
  if (cause.startsWith('unpin:')) return 'Unpinned';
  if (cause.startsWith('deploy-config:')) return 'Deploy config updated';
  return cause.length > 40 ? cause.slice(0, 40) + '...' : cause;
}

function promptLabel(cause: string): string {
  if (cause === 'bootstrap') return 'Your initial idea';
  if (cause.startsWith('edit:')) return 'Your improvement prompt';
  if (cause.startsWith('answer:')) return 'Your answers';
  return 'Your prompt';
}

function isHtmlOrSvg(name: string): boolean {
  return /\.(html?|svg)$/i.test(name);
}
